import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import CopyButton from '../CopyButton';
import { formatEvaluationForCopy } from '../utils';

/**
 * Score display section with copy functionality
 */
const EvaluationScores = ({ result, score, CategoryBadge, ScoreBadge, onCopyFull }) => {
  const copyText = useMemo(() => {
    return formatEvaluationForCopy(result, score);
  }, [result, score]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-xs text-gray-500 mb-1">Overall Score</p>
          <ScoreBadge score={score} />
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Correctness</p>
          <p className="text-lg font-semibold text-white">
            {result.evaluation?.correctness != null 
              ? ((result.evaluation.correctness * 100).toFixed(1) + '%') 
              : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Relevance</p>
          <p className="text-lg font-semibold text-white">
            {result.evaluation?.relevance != null 
              ? ((result.evaluation.relevance * 100).toFixed(1) + '%') 
              : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Guidelines</p>
          <p className="text-lg font-semibold text-white">
            {result.evaluation?.guidelines_adherence != null 
              ? ((result.evaluation.guidelines_adherence * 100).toFixed(1) + '%') 
              : 'N/A'}
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-2">Category</h3>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryBadge category={result.case_metadata?.category} />
            {result.case_metadata?.expected_quality && (
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs capitalize ${
                  result.case_metadata.expected_quality === 'high'
                    ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40'
                    : result.case_metadata.expected_quality === 'medium'
                    ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                    : 'bg-gray-500/15 text-gray-300 border-gray-500/40'
                }`}
                title="Test case designer's expectation of coach response quality for this case type"
              >
                Expected Quality: {result.case_metadata.expected_quality}
              </span>
            )}
            {result.prompt_type && (
              <span className="inline-flex items-center rounded-full border border-blue-500/40 bg-blue-500/15 px-3 py-1 text-xs font-medium text-blue-200 font-mono">
                {result.prompt_type}
              </span>
            )}
          </div>
          <button
            onClick={() => onCopyFull(copyText)}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800/50 border border-gray-700 rounded-lg hover:bg-gray-700/50 hover:border-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
            aria-label="Copy full evaluation for LLM analysis"
            title="Copy full evaluation for LLM analysis"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy for Analysis
          </button>
        </div>
      </div>
    </>
  );
};

EvaluationScores.propTypes = {
  result: PropTypes.object.isRequired,
  score: PropTypes.number.isRequired,
  CategoryBadge: PropTypes.elementType.isRequired,
  ScoreBadge: PropTypes.elementType.isRequired,
  onCopyFull: PropTypes.func.isRequired,
};

export default React.memo(EvaluationScores);

